import React from "react";
import { Dropdown, Checkbox, Button, Input, Icon, Label, SemanticICONS } from "semantic-ui-react";
import { useBCGlobalReducer } from "./BCGlobalStore";
import { GlobalConstants } from "./constants/GlobalConstants";
import BCMenuBar from "./ui/BCMenuBar";
import CurrencyInput from "react-currency-input-field";
import {
  getFilterMenuTitle,
  getUpdatedCheckedFilters,
  getUpdatedClearedMenuFilters,
  getUpdatedDateFilters,
  getUpdatedOnCollapseFilters,
  getUpdatedSelectedDateFilters,
} from "../helpers/GlobalStorageHelper";
import ErrorBoundary from "./BCErrorBoundary";
import "../styles/global/filter.scss";
import { BCFilterSearchBar } from "./BCFilterSearch";
import { arrayHasItems } from "../helpers/GlobalHelper";

const FilterComponentCheckboxItem = ({ option, menuTitle, filtersList, dispatch }) => {
  const handleCheckChange = (data, subgroup = "") => {
    dispatch({
      type: GlobalConstants.BCStoreReducerCases.SET_GLOBAL_FILTERS,
      payload:
        getUpdatedCheckedFilters(filtersList, menuTitle, data, subgroup) ||
        filtersList,
    });
  };

  const handleCollapseClick = (subgroup) => {
    dispatch({
      type: GlobalConstants.BCStoreReducerCases.SET_GLOBAL_FILTERS,
      payload:
        getUpdatedOnCollapseFilters(filtersList, menuTitle, subgroup) ||
        filtersList,
    });
  };

  const isSubgroupActive = option?.subgroupOptions?.some((so) => so.value);
  const subgroupLabel = () => {
    if (isSubgroupActive) {
      const activeOptions = option?.subgroupOptions?.reduce((total, so) => {
        if (so.value) {
          return total + 1;
        }
        return total;
      }, 0);
      return `${option?.label} (${activeOptions})`;
    }
    return option?.label;
  };

  if (option?.subgroupOptions) {
    return (
      <Dropdown.Item
        className="no_padding"
        onClick={(e) => e.stopPropagation()}
        key={`${option?.label}`}
      >
        <div
          className="checkbox-collapsible-title"
          onClick={() => handleCollapseClick(option?.label)}
        >
          <Icon name={option?.isCollapsed ? GlobalConstants.Icons.CHEVRON_DOWN as SemanticICONS : GlobalConstants.Icons.CHEVRON_UP as SemanticICONS}/>
          <span className={`${isSubgroupActive ? "bold" : ""}`}>
            {subgroupLabel()}
          </span>
        </div>
        <div className={option?.isCollapsed ? `${"display-none"}` : ""}>
          {option?.subgroupOptions?.map((so) => {
            return (
              <Dropdown.Item
                className="no_padding"
                onClick={(e) => e.stopPropagation()}
                key={`${so.label}`}
              >
                <Checkbox
                  label={so.label}
                  optionId={so.id}
                  onChange={(_, data) => handleCheckChange(data, option?.label)}
                  checked={so.value}
                  className="subgroup-element"
                />
              </Dropdown.Item>
            );
          })}
        </div>
      </Dropdown.Item>
    );
  }

  return (
    <Dropdown.Item className="no_padding" onClick={(e) => e.stopPropagation()}>
      <Checkbox
        label={option.label}
        optionId={option.id}
        onChange={(_, data) => handleCheckChange(data)}
        checked={option.value}
        className="checkbox-fill"
      />
    </Dropdown.Item>
  );
};

const FilterComponentDateAmountItem = ({ options, value: selectedValue, filterType, selectedIndex, menuTitle, filtersList, dispatch }) => {
  const onChange: any = (_, { value }) => {
    let selectedIndex = 0;
    options.map(({ text }, i) => {
      if (value === text) {
        selectedIndex = i;
      }
    });

    dispatch({
      type: GlobalConstants.BCStoreReducerCases.SET_GLOBAL_FILTERS,
      payload:
        getUpdatedSelectedDateFilters(
          filtersList,
          menuTitle,
          value,
          selectedIndex
        ) || filtersList,
    });
  };

  const handleDateAmountChange = (value, id) => {
    const updatedFilters =
      getUpdatedDateFilters(filtersList, menuTitle, selectedValue, value, id) ||
      filtersList;

    dispatch({
      type: GlobalConstants.BCStoreReducerCases.SET_GLOBAL_FILTERS,
      payload: updatedFilters,
    });
  };

  const getDropdownOptions = (options) => {
    return options.map(({ text, fields }) => {
      return {
        key: text,
        text,
        value: text,
        fields: JSON.stringify(fields),
      };
    });
  };

  return (
    <Dropdown.Item
      className="no_hover no_padding"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="date-item-container">
        <div className="date-select-dropdown-container">
          <div className="date-select-dropdown-label">Operator</div>
          <div className="dropdown-icon-wrapper">
            <Icon name={"chevron down"}/>
            <Dropdown
              onChange={onChange}
              options={getDropdownOptions(options)}
              value={selectedValue}
              className="custom-dropdown"
              fluid
            />
          </div>
        </div>
        <div className="date-fields-inline-container">
          {options[selectedIndex]?.fields?.map((df, i) => {
            return (
              <div className="date-input-container" key={`${df?.label}_${i}`}>
                <div className="date-input-label">{df?.label || ""}</div>
                {filterType === "date" && (
                  <Input
                    className="date-input-field"
                    size="small"
                    type="date"
                    name={df?.label}
                    value={df?.value}
                    onChange={({ currentTarget }) =>
                      handleDateAmountChange(currentTarget?.value, df.id)
                    }
                  />
                )}
                {filterType === "amount" && (
                  <React.Fragment>
                    <Input
                      className="amount-input-field"
                      size="small"
                      type="number"
                      name={df?.label}
                      value={df?.value}
                      onChange={({ currentTarget }) =>
                        handleDateAmountChange(currentTarget?.value, df.id)
                      }
                      labelPosition="left"
                    >
                      <Label basic>$</Label>
                      <CurrencyInput
                        className="primary-currency-input"
                        allowDecimals={true}
                        decimalsLimit={2}
                        decimalScale={2}
                        value={df?.value}
                        onValueChange={(value) =>
                          handleDateAmountChange(value, df.id)
                        }
                        allowNegativeValue={false}
                      />
                    </Input>
                  </React.Fragment>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Dropdown.Item>
  );
};

const BCFilter = ({ ...props }) => {
  const [{ filtersList, filtersListInitial }, dispatch]: any = props.bcGlobalState;
  const handleResetClick = () => dispatch({ type: GlobalConstants.BCStoreReducerCases.RESET_GLOBAL_FILTERS });
  const handleClearClick = (title) => {
    const updatedFilters =
      getUpdatedClearedMenuFilters(
        filtersList,
        filtersListInitial,
        title
      ) || filtersList;

    dispatch({
      type: GlobalConstants.BCStoreReducerCases.SET_GLOBAL_FILTERS,
      payload: updatedFilters,
    });
  };

  return (
    <div className="filter_container">
      <div className="filter_list_container">
        {filtersList?.map(
          ({ title, filterType, options, isDisabled, value, selectedIndex, isHidden }) => {
            const { formattedTitle, isFilterActive } = getFilterMenuTitle(
              filtersList,
              title
            );
            if (isDisabled === false && !isHidden) {
              return (
                <Dropdown
                  text={formattedTitle}
                  multiple
                  pointing="top right"
                  className={`item${isFilterActive ? " bold" : ""}`}
                  key={`filter-component-container${title}`}
                  disabled={!options || options?.length === 0}
                >
                  <Dropdown.Menu direction={"left"}>
                    <div className="dropdown-menu-container">
                      {filterType === "checkbox" && (
                        <React.Fragment>
                          {options.map((option, i) => {
                            return (
                              <FilterComponentCheckboxItem
                                key={`filter-component-item${option.label}${i}`}
                                option={option}
                                menuTitle={title}
                                filtersList={filtersList}
                                dispatch={dispatch}
                              />
                            );
                          })}
                        </React.Fragment>
                      )}
                      {(filterType === "date" || filterType === "amount") && (
                        <FilterComponentDateAmountItem
                          options={options}
                          value={value}
                          filterType={filterType}
                          selectedIndex={selectedIndex}
                          menuTitle={title}
                          filtersList={filtersList}
                          dispatch={dispatch}
                        />
                      )}
                    </div>
                    <Dropdown.Item
                      className="no_hover no_padding"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="filter_clear_button_container">
                        <span
                          className="filter_clear_button"
                          onClick={() => handleClearClick(title)}
                        >
                          Clear X
                        </span>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              );
            }
          }
        )}
      </div>
      <Button
        compact
        className="filter_clear_all_button"
        onClick={handleResetClick}
      >
        Reset
      </Button>
    </div>
  );
};

export default BCFilter;

export const BCFilterBar = ({ ...props }) => {
  const bcGlobalState = useBCGlobalReducer();
  const bcReducerOptions = props.reducerInstance ? props.reducerInstance : bcGlobalState;
  const  [{ isFilterOpen, dataToFilter }] = bcReducerOptions;

  return (
    <React.Fragment>
      {(isFilterOpen && arrayHasItems(dataToFilter)) && (
        <ErrorBoundary>
          <BCMenuBar
            leftChildren={[<React.Fragment/>]}
            rightChildren={[<BCFilter bcGlobalState={bcReducerOptions}/>]}
            className={"no-top-margin"}
          />
        </ErrorBoundary>
      )}
    </React.Fragment>
  );
};

interface IBCFilterSearchBarProps {
  searchBarLeftComponent?: JSX.Element;
  reducerInstance?: [any, (any) => any];
  hideFilterBar?: boolean
}

export const BCFilterSearchGroupBar = ({ searchBarLeftComponent, reducerInstance, hideFilterBar = false }: IBCFilterSearchBarProps) => {
  return (
    <React.Fragment>
      <BCFilterSearchBar reducerInstance={reducerInstance} searchBarLeftComponent={searchBarLeftComponent} hideFilterBar={hideFilterBar}/>
      {!hideFilterBar && <BCFilterBar reducerInstance={reducerInstance}/>}
    </React.Fragment>
  );
};